import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getIPFilters, updateIPFilters } from '@/lib/api/api'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'
import { SettingsWell, SettingsCard, SettingsRow, settingsButtonClass } from '@/components/shared/settings/SettingsKit'

interface IPFilteringTabProps {
  domain: string
  propertyId?: string
}

export default function IPFilteringTab({ propertyId }: IPFilteringTabProps) {
  const { token } = useAuth()

  const [ipAddresses, setIpAddresses] = useState<string[]>([])
  const [newIpAddress, setNewIpAddress] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadIPFilters()
  }, [propertyId, token])

  const loadIPFilters = async () => {
    if (!token || !propertyId) return

    try {
      setIsLoading(true)
      const filters = await getIPFilters(propertyId, token)
      setIpAddresses(filters || [])
    } catch (error) {
      console.error('Error loading IP filters:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const validateIP = (ip: string): boolean => {
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/

    if (!ipv4Regex.test(ip) && !ipv6Regex.test(ip)) {
      return false
    }

    if (ipv4Regex.test(ip)) {
      const parts = ip.split('.')
      return parts.every((part) => {
        const num = parseInt(part, 10)
        return num >= 0 && num <= 255
      })
    }

    return true
  }

  const addInputField = async () => {
    const ip = newIpAddress.trim()

    if (!ip) {
      toast.error('Enter an IP address')
      return
    }

    if (!validateIP(ip)) {
      toast.error("That doesn't look like an IPv4 or IPv6 address")
      return
    }

    if (ipAddresses.includes(ip)) {
      toast.error('That IP is already excluded')
      return
    }

    if (!token || !propertyId) return

    setIsSubmitting(true)
    try {
      const updated = [...ipAddresses, ip]
      await updateIPFilters(propertyId, updated, token)
      setIpAddresses(updated)
      setNewIpAddress('')
      toast.success('IP excluded from tracking')
    } catch (error) {
      console.error('Error adding IP filter:', error)
      toast.error("Couldn't add the IP")
    } finally {
      setIsSubmitting(false)
    }
  }

  const removeInputField = async (index: number) => {
    if (!token || !propertyId) return

    setIsSubmitting(true)
    try {
      const updated = ipAddresses.filter((_, i) => i !== index)
      await updateIPFilters(propertyId, updated, token)
      setIpAddresses(updated)
      toast.success('IP removed')
    } catch (error) {
      console.error('Error removing IP filter:', error)
      toast.error("Couldn't remove the IP")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      addInputField()
    }
  }

  return (
    <SettingsWell>
      <SettingsCard>
        <SettingsRow
          title="Excluded IPs"
          description="Visits from these addresses are left out of every report. Add your office, VPN or home IP."
        >
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Input
                type="text"
                aria-label="IP address to exclude"
                placeholder="e.g. 192.168.1.1"
                value={newIpAddress}
                onChange={(e) => setNewIpAddress(e.target.value)}
                className="h-9 flex-1 rounded-lg font-mono text-[13px]"
                onKeyDown={handleKeyPress}
              />
              <Button
                variant="outline"
                onClick={addInputField}
                disabled={isSubmitting || !newIpAddress.trim()}
                className={`${settingsButtonClass} h-9 gap-1.5`}
              >
                {isSubmitting && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
                Add IP
              </Button>
            </div>

            {isLoading ? (
              <div className="flex flex-col gap-px overflow-hidden rounded-lg border border-border">
                <div className="h-10 animate-pulse bg-muted/60" />
                <div className="h-10 animate-pulse bg-muted/40" />
              </div>
            ) : ipAddresses.length > 0 && (
              <ul className="flex flex-col rounded-lg border border-border">
                {ipAddresses.map((ip, index) => (
                  <li key={ip} className="flex h-10 items-center justify-between gap-3 border-t border-border pl-3 pr-1.5 first:border-t-0">
                    <span className="truncate font-mono text-[13px] tabular-nums text-foreground">{ip}</span>
                    <button
                      type="button"
                      onClick={() => removeInputField(index)}
                      disabled={isSubmitting}
                      aria-label={`Remove ${ip}`}
                      className="h-7 shrink-0 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!isLoading && (
              <p className="text-xs leading-4 text-foreground/60">
                {ipAddresses.length === 0
                  ? 'No IPs excluded yet. Internal visits count toward your numbers until you add one.'
                  : `${ipAddresses.length} excluded`}
              </p>
            )}
          </div>
        </SettingsRow>
      </SettingsCard>
    </SettingsWell>
  )
}
